/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/naming-convention */
import type { Component, JSX } from 'solid-js';
import { createEffect, createSignal } from 'solid-js';
export interface CustomTextareaProps {
  name: string;
  label: string;
  setFocus?: boolean;

  id?: string;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  rows?: number;
  maxLength?: number;
  'data-cy'?: string;
  dataCy?: string;

  error?: string;
  touched?: boolean;

  onInput?(
    event: InputEvent & { currentTarget: HTMLTextAreaElement; target: Element }
  ): void;
  onChange?(
    event: Event & { currentTarget: HTMLTextAreaElement; target: Element }
  ): void;
  onBlur?(
    event: FocusEvent & { currentTarget: HTMLTextAreaElement; target: Element }
  ): void;
  onFocus?(
    event: FocusEvent & { currentTarget: HTMLTextAreaElement; target: Element }
  ): void;
}

const CustomTextarea: Component<CustomTextareaProps> = (props) => {
  let textareaRef: HTMLTextAreaElement | undefined;
  const [innerValue, setInnerValue] = createSignal<string>('');

  createEffect(() => {
    if (props.setFocus && textareaRef) {
      textareaRef.focus();
    }
  });

  const getId = () => props.id ?? props.name;
  const getDataCy = () => props.dataCy ?? props['data-cy'] ?? props.name;
  const getValue = () =>
    props.value !== undefined ? props.value : innerValue();
  const hasError = () => Boolean(props.touched && props.error);
  const inputClass = () => (hasError() ? 'error' : '');

  const emit = (
    eventName: string,
    event: Event,
    detail?: Record<string, unknown>
  ): void => {
    const customEvent = new CustomEvent(eventName, {
      detail: { ...(detail ?? {}), originalEvent: event },
      bubbles: true,
    });
    (event.target as HTMLElement | null)?.dispatchEvent(customEvent);
  };

  const handleInput: JSX.EventHandlerUnion<HTMLTextAreaElement, InputEvent> = (
    e
  ) => {
    const target = e.currentTarget as HTMLTextAreaElement;
    setInnerValue(target.value);
    emit('tc-customtextarea-input', e as unknown as Event, {
      value: target.value,
    });
    props.onInput?.(
      e as unknown as InputEvent & {
        currentTarget: HTMLTextAreaElement;
        target: Element;
      }
    );
  };

  const handleChange: JSX.EventHandlerUnion<HTMLTextAreaElement, Event> = (
    e
  ) => {
    const target = e.currentTarget as HTMLTextAreaElement;
    emit('tc-customtextarea-change', e as Event, { value: target.value });
    props.onChange?.(
      e as Event & { currentTarget: HTMLTextAreaElement; target: Element }
    );
  };

  const handleBlur: JSX.EventHandlerUnion<HTMLTextAreaElement, FocusEvent> = (
    e
  ) => {
    emit('tc-customtextarea-blur', e as FocusEvent);
    props.onBlur?.(
      e as FocusEvent & { currentTarget: HTMLTextAreaElement; target: Element }
    );
  };

  const handleFocus: JSX.EventHandlerUnion<HTMLTextAreaElement, FocusEvent> = (
    e
  ) => {
    emit('tc-customtextarea-focus', e as FocusEvent);
    props.onFocus?.(
      e as FocusEvent & { currentTarget: HTMLTextAreaElement; target: Element }
    );
  };

  return (
    <div class="input-group">
      <label for={getId()}>{props.label}</label>
      <div class="input-focus-group">
        <textarea
          id={getId()}
          name={props.name}
          class={`pl-1 pt-1 ${inputClass()}`}
          ref={(el) => (textareaRef = el)}
          value={getValue()}
          placeholder={props.placeholder}
          disabled={props.disabled}
          readonly={props.readonly}
          required={props.required}
          rows={props.rows}
          maxlength={props.maxLength}
          data-cy={getDataCy()}
          onInput={handleInput}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
        <div class="line" />
      </div>
      {hasError() && <div class="error-message">{props.error}</div>}
    </div>
  );
};

export default CustomTextarea;
